import secureApiService from './secureApiService'
import { Department, DepartmentJSON } from '@/models/department.model'
import { PaginatedResponse, fetchAllPages } from '@/models/templates/pagination.model'

export interface DepartmentMutationPayload {
  facultyId: string
  name: string
  description?: string | null
  isActive?: boolean
}

interface DepartmentsResponseJSON {
  success: boolean
  message?: string
  data?: DepartmentJSON[] | DepartmentJSON | null
  meta?: {
    total?: number
    page?: number
    perPage?: number
  }
}

interface DepartmentSingleResponseJSON {
  success: boolean
  message?: string
  data?: DepartmentJSON
}

class DepartmentsService {
  private basePath = '/departments'

  async getDepartmentsPage(params: { page?: number; perPage?: number } = {}): Promise<PaginatedResponse<Department>> {
    const page = params.page ?? 1
    const perPage = params.perPage ?? 25
    const query = new URLSearchParams({ page: String(page), perPage: String(perPage) })

    const response = await secureApiService.get<DepartmentsResponseJSON>(`${this.basePath}?${query.toString()}`, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch departments')
    }

    const items = Array.isArray(payload.data) ? payload.data : payload.data ? [payload.data] : []
    const data = items.map((department) => Department.fromJSON(department))
    const meta = {
      total: payload.meta?.total ?? items.length,
      page: payload.meta?.page ?? page,
      perPage: payload.meta?.perPage ?? perPage,
    }

    return { data, meta }
  }

  async getAllDepartments(perPage: number = 50): Promise<PaginatedResponse<Department>> {
    return fetchAllPages((page) => this.getDepartmentsPage({ page, perPage }), perPage)
  }

  async getDepartments(): Promise<Department[]> {
    const response = await this.getAllDepartments()
    return response.data
  }

  async getDepartment(id: string): Promise<Department> {
    const response = await secureApiService.get<DepartmentSingleResponseJSON>(`${this.basePath}/${id}`, {
      requireAuth: true,
    })

    const body = response.data

    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to fetch department details')
    }

    return Department.fromJSON(body.data)
  }

  async createDepartment(payload: DepartmentMutationPayload): Promise<Department> {
    const response = await secureApiService.post<DepartmentSingleResponseJSON>(this.basePath, {
      facultyId: payload.facultyId,
      name: payload.name,
      description: payload.description,
      isActive: payload.isActive,
    })

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to create department')
    }

    return Department.fromJSON(body.data)
  }

  async updateDepartment(id: string, payload: DepartmentMutationPayload): Promise<Department> {
    const response = await secureApiService.put<DepartmentSingleResponseJSON>(`${this.basePath}/${id}`, {
      facultyId: payload.facultyId,
      name: payload.name,
      description: payload.description,
      isActive: payload.isActive,
    })

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to update department')
    }

    return Department.fromJSON(body.data)
  }

  async deleteDepartment(id: string): Promise<void> {
    const response = await secureApiService.delete<DepartmentSingleResponseJSON>(`${this.basePath}/${id}`)

    const body = response.data
    if (!body?.success) {
      throw new Error(body?.message || 'Unable to delete department')
    }
  }
}

export const departmentsService = new DepartmentsService()
